import { isDiscontinued } from '@/lib/app-categories/discontinued'
import {
  Classification,
  Recommendation,
  RECOMMENDATION_SORT_ORDER,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { queryPayload } from '@/lib/payloadClient'
import {
  APP_RANKING_SNAPSHOT_LOOKBACK,
  type AppRankingSnapshotInput,
} from '@/lib/rankings/appRanking'
import type {
  App,
  AppCategoryRecommendation,
  Category,
  Comparison,
  ComparisonAppsApp,
  Video,
  VideoAppsApp,
} from '@/payload-types'

/**
 * Client-side query to fetch apps by slugs with full review data
 * Used in dynamic mode for Editor's Verdict section
 */
export async function queryAppsBySlugsWithReviewData({ slugs }: { slugs: Array<App['slug']> }) {
  const { docs: apps } = await queryPayload({
    collection: 'app',
    select: {
      name: true,
      icon: true,
      glyph: true,
      slug: true,
      screenshots: true,
      categoryRecommendations: true,
      brandPrimaryColor: true,
      brandSecondaryColor: true,
      brandTertiaryColor: true,
      brandBackgroundColor: true,
      review: true,
      deals: true,
      affiliateLink: true,
      partnershipTier: true,
    },
    populate: {
      deal: {
        name: true,
        unitType: true,
        isReward: true,
        quantity: true,
        durationDays: true,
        product: true,
        startDate: true,
        endDate: true,
        order: true,
        featuredOrder: true,
      },
      review: {
        bestFor: true,
        reviewCategoryCriterionRating: true,
        summary: true,
      },
      review_categoryCriterion_rating: {
        categoryCriterion: true,
        rating: true,
        summary: true,
      },
      app_category_recommendation: {
        classification: true,
        discontinuedAt: true,
        recommendation: true,
        category: true,
        bestFor: true,
        summary: true,
      },
      category: {
        name: true,
        slug: true,
        categoryCriteria: true,
      },
      category_criteria_criterion: {
        criterion: true,
        category: true,
      },
      criterion: {
        name: true,
        icon: true,
      },
      media: {
        url: true,
        alt: true,
        filename: true,
        coveragePercent: true,
        xOffsetPercent: true,
        yOffsetPercent: true,
      },
    },
    where: { slug: { in: slugs } },
    pagination: false,
    // Must stay at a depth of 6 regardless of what code review tools say
    depth: 6, // Increased to ensure full category chain is populated for extractUniqueCriteriaFromApp
    cacheKey: `query:app:review-data:${[...slugs].sort().join(',')}`,
  })

  return apps as App[]
}

export async function queryComparisonById(id: Comparison['id']): Promise<Comparison | null> {
  const { docs: comparisons } = await queryPayload({
    collection: 'comparison',
    select: {
      comparisonApps: true,
      name: true,
      aiSummary: true,
      tldr: true,
      verdictSummary: true,
      updatedAt: true,
    },
    populate: {
      comparison_apps_app: {
        app: true,
        order: true,
      },
      comparison_categoryCriteria: {
        categoryCriterion: true,
        summary: true,
        _status: true,
      },
      app: {
        icon: true,
        glyph: true,
        slug: true,
        name: true,
        screenshots: true,
        categoryRecommendations: true,
        brandPrimaryColor: true,
        brandSecondaryColor: true,
        brandTertiaryColor: true,
        brandBackgroundColor: true,
        review: true,
      },
      review: {
        bestFor: true,
        reviewCategoryCriterionRating: true,
        summary: true,
      },
      review_categoryCriterion_rating: {
        categoryCriterion: true,
        rating: true,
        summary: true,
      },
      app_category_recommendation: {
        classification: true,
        discontinuedAt: true,
        recommendation: true,
        bestFor: true,
        summary: true,
        order: true,
        category: true,
      },
      category: {
        name: true,
        slug: true,
        icon: true,
        isFeatured: true,
        categoryCriteria: true,
      },
      category_criteria_criterion: {
        criterion: true,
        category: true,
      },
      criterion: {
        name: true,
        icon: true,
      },
      media: {
        url: true,
        alt: true,
        filename: true,
        coveragePercent: true,
        xOffsetPercent: true,
        yOffsetPercent: true,
      },
    },
    where: { id: { equals: id } },
    pagination: false,
    depth: 6,
    cacheKey: `query:comparison:id:${id}`,
  })

  const [comparison] = comparisons

  return comparison || null
}

/**
 * check if a comparison exists for given app slugs.
 * Returns the comparison with full data if found, null otherwise.
 * Used to determine if Editor's Verdict should show after swapping apps.
 *
 * Note: This finds comparisons containing ALL given slugs regardless of order.
 * e.g., searching for ["gmail", "superhuman"] will find "superhuman-vs-gmail" comparison.
 * We also verify the comparison has exactly these apps (not more).
 *
 * Uses junction table query pattern (from slug-order.test.ts) which is more reliable
 * than virtual join path queries for finding comparisons by app slugs.
 */
export async function queryComparisonByAppSlugs({ slugs }: { slugs: Array<App['slug']> }) {
  if (slugs.length < 2) return null

  // Step 1: Get app IDs from slugs
  const { docs: apps } = await queryPayload({
    collection: 'app',
    select: { slug: true },
    where: { slug: { in: slugs } },
    pagination: false,
  })

  // If we didn't find all the apps, there can't be a comparison
  if (apps.length !== slugs.length) return null

  const appIds = apps.map((a) => a.id)

  // Step 2: Query junction table to find comparisons containing these apps
  const { docs: comparisonApps } = await queryPayload({
    collection: 'comparison_apps_app',
    select: {
      comparison: true,
      app: true,
      order: true,
    },
    where: { app: { in: appIds } },
    pagination: false,
  })

  if (comparisonApps.length === 0) return null

  // Step 3: Group by comparison ID
  const comparisonGroups: Record<number, ComparisonAppsApp[]> = {}
  for (const record of comparisonApps) {
    const comparisonId =
      typeof record.comparison === 'number' ? record.comparison : record.comparison?.id
    if (!comparisonId) continue

    if (!comparisonGroups[comparisonId]) {
      comparisonGroups[comparisonId] = []
    }
    comparisonGroups[comparisonId].push(record)
  }

  // Step 4: Find the comparison that has exactly all our apps (not more, not fewer)
  let matchingComparisonId: number | null = null

  for (const comparisonId in comparisonGroups) {
    const records = comparisonGroups[comparisonId]

    // Must have exactly the same number of apps
    if (records.length !== appIds.length) continue

    const recordAppIds = records
      .map((r) => (typeof r.app === 'number' ? r.app : r.app?.id))
      .filter((id): id is number => id !== undefined)

    // Check if this comparison has exactly the apps we're looking for
    const hasAllApps = appIds.every((id) => recordAppIds.includes(id))

    if (hasAllApps) {
      matchingComparisonId = Number(comparisonId)
      break
    }
  }

  if (!matchingComparisonId) return null

  // Step 5: Fetch the full comparison by ID with all populate fields
  return queryComparisonById(matchingComparisonId)
}

export async function queryRelatedAppsByOverlappingCategoryIds(categoryIds: Array<Category['id']>) {
  if (!categoryIds.length) return [] as App[]

  const { docs: acrDocs } = await queryPayload({
    collection: 'app_category_recommendation',
    populate: {
      app: {
        icon: true,
        glyph: true,
        slug: true,
        name: true,
        review: true,
        brandPrimaryColor: true,
        brandSecondaryColor: true,
        brandTertiaryColor: true,
        brandBackgroundColor: true,
        categoryRecommendations: true,
        partnershipTier: true,
      },
      app_category_recommendation: {
        category: true,
        classification: true,
        discontinuedAt: true,
        recommendation: true,
        bestFor: true,
      },
      category: {
        name: true,
        slug: true,
        categoryCriteria: true,
      },
      // Needed for extractUniqueCriteriaFromApps to show "Efficient at..." in CompareHero
      category_criteria_criterion: {
        criterion: true,
        category: true,
      },
      criterion: {
        name: true,
        icon: true,
      },
      media: {
        url: true,
        alt: true,
        filename: true,
        coveragePercent: true,
        xOffsetPercent: true,
        yOffsetPercent: true,
      },
      review: {
        bestFor: true,
      },
    },
    where: { category: { in: categoryIds }, discontinuedAt: { exists: false } },
    pagination: false,
    depth: 5, // Ensures nested category chain is populated for extractUniqueCriteriaFromApp
    cacheKey: `query:acr:related:${[...categoryIds].sort().join(',')}`,
  })

  const orderedApps = acrDocs
    .sort((a, b) => {
      const recA = (a as unknown as AppCategoryRecommendation).recommendation as Recommendation
      const recB = (b as unknown as AppCategoryRecommendation).recommendation as Recommendation
      return (RECOMMENDATION_SORT_ORDER[recA] ?? 999) - (RECOMMENDATION_SORT_ORDER[recB] ?? 999)
    })
    .map((acr) => acr.app as App)
    // remove duplicates
    .filter((app, index, self) => index === self.findIndex((a) => a.id === app.id))
    // remove apps without reviews
    .filter((app) => app.review)

  return orderedApps
}

// Full main-list app_category_recommendation set for a category, for deriving each
// app's true /best/[category] rank (via orderAppsByRecommendation) rather than its
// position within just the compared apps. Also carries the fields `deriveAppRanking`/
// `resolveAppRankChange` need (rankDeltaOverride, displayRankDelta, updatedAt) so the
// rank-change trend arrow can be derived the same way as the /best page.
//
// Deliberately uncached (no `cacheKey`): there's no invalidation hook on
// category_rank_snapshot writes, so a Redis-cached read here would drift out of
// sync with the /best page's always-live `payload.find()` — showing a stale or
// wrong trend direction. Matches the uncached precedent in getAppRanking.client.ts.
export async function queryCategoryRankingsByCategoryId(
  categoryId: Category['id'],
): Promise<AppCategoryRecommendation[]> {
  const { docs: acrDocs } = await queryPayload({
    collection: 'app_category_recommendation',
    select: {
      app: true,
      classification: true,
      discontinuedAt: true,
      recommendation: true,
      order: true,
      displayRankDelta: true,
      rankDeltaOverride: true,
      updatedAt: true,
    },
    populate: {
      app: { slug: true },
    },
    where: { category: { equals: categoryId } },
    depth: 1,
    pagination: false,
  })

  return acrDocs
}

// Rank-change snapshots for a category, newest first — the input `deriveAppRanking`
// needs to work out each app's organic rank trend (mirrors the /best page's own
// snapshot read in `fetchSnapshotRankChangesByAcrId`). Uncached for the same reason
// as above.
export async function queryCategoryRankSnapshotsByCategoryId(
  categoryId: Category['id'],
): Promise<AppRankingSnapshotInput[]> {
  const { docs: snapshots } = await queryPayload({
    collection: 'category_rank_snapshot',
    select: { snapshotDate: true, rankings: true },
    where: { category: { equals: categoryId } },
    sort: ['-snapshotDate', '-createdAt'],
    depth: 0,
    limit: APP_RANKING_SNAPSHOT_LOOKBACK,
    pagination: false,
  })

  return snapshots
}

/**
 * Client-side query to fetch trending comparisons
 * Used in dynamic mode when comparison doesn't exist in DB
 * mimics the query used in the server-side generated page
 */
export async function queryTrendingComparisons({
  appIds,
  currentComparisonId,
}: {
  appIds: Array<App['id']>
  currentComparisonId?: Comparison['id']
}) {
  // Find comparison IDs that contain at least one of the current apps
  const { docs: junctionDocs } = await queryPayload({
    collection: 'comparison_apps_app',
    select: { comparison: true },
    populate: { comparison: {} },
    where: { app: { in: appIds } },
    pagination: false,
  })

  const relatedComparisonIds = [
    ...new Set(
      junctionDocs
        .map((doc) => (typeof doc.comparison === 'number' ? doc.comparison : doc.comparison?.id))
        .filter((id): id is number => id !== undefined && id !== currentComparisonId),
    ),
  ]

  if (relatedComparisonIds.length === 0) return []

  const { docs: trendingComparisons } = await queryPayload({
    collection: 'comparison',
    populate: {
      comparison_apps_app: {
        app: true,
        order: true,
      },
      app: {
        icon: true,
        glyph: true,
        slug: true,
        name: true,
        screenshots: true,
        categoryRecommendations: true,
        brandPrimaryColor: true,
        brandSecondaryColor: true,
        brandTertiaryColor: true,
        brandBackgroundColor: true,
        review: true,
      },
      review: {
        bestFor: true,
      },
      app_category_recommendation: {
        classification: true,
        discontinuedAt: true,
        recommendation: true,
        bestFor: true,
        summary: true,
        order: true,
        category: true,
      },
      category: {
        name: true,
        slug: true,
        icon: true,
        isFeatured: true,
        categoryCriteria: true,
      },
      category_criteria_criterion: {
        criterion: true,
        category: true,
      },
      criterion: {
        name: true,
        icon: true,
      },
      media: {
        url: true,
        alt: true,
        filename: true,
        coveragePercent: true,
        xOffsetPercent: true,
        yOffsetPercent: true,
      },
    },
    where: { id: { in: relatedComparisonIds } },
    depth: 6,
    limit: 3,
    pagination: false,
  })

  return trendingComparisons as Comparison[]
}

/**
 * When classification and recommendation are both provided, categories are
 * prioritized by classification: Primary+Primary > Primary+Secondary > Secondary+Secondary
 */
export async function queryOverlappingAppCategories({ appIds }: { appIds: Array<App['id']> }) {
  const { docs: acrDocs } = await queryPayload({
    collection: 'app_category_recommendation',
    select: {
      app: true,
      category: true,
      classification: true,
      discontinuedAt: true,
      recommendation: true,
      bestFor: true,
    },
    populate: {
      app: {},
      category: {
        name: true,
        slug: true,
        icon: true,
      },
    },
    where: { app: { in: appIds }, discontinuedAt: { exists: false } },
    depth: 1,
    pagination: false,
    cacheKey: `query:acr:overlap:${[...appIds].sort().join(',')}`,
  })

  type AppCategoryInfo = { classification: Classification; recommendation: Recommendation }

  const { categoryDataMap } = acrDocs.reduce(
    (acc, doc) => {
      if (isDiscontinued(doc) || !doc.category || !doc.app) return acc

      const appId = (doc.app as App).id
      const category = doc.category as Category
      const categoryId = category.id
      const docClassification = doc.classification as Classification
      const docRecommendation = doc.recommendation as Recommendation

      if (!acc.categoryDataMap.has(categoryId)) {
        acc.categoryDataMap.set(categoryId, {
          category,
          appInfo: new Map(),
        })
      }

      const categoryData = acc.categoryDataMap.get(categoryId)!
      if (!categoryData.appInfo.has(appId)) {
        categoryData.appInfo.set(appId, {
          classification: docClassification,
          recommendation: docRecommendation,
        })
      }

      return acc
    },
    {
      categoryDataMap: new Map<
        Category['id'],
        {
          category: Category
          appInfo: Map<App['id'], AppCategoryInfo>
        }
      >(),
    },
  )

  // Filter to only overlapping categories (all compared apps must be present)
  const overlappingCategoryData = Array.from(categoryDataMap.values()).filter(
    (data) => data.appInfo.size >= appIds.length,
  )

  const recommendationScore: Record<Recommendation, number> = {
    [Recommendation.TopPick]: 4,
    [Recommendation.Recommended]: 3,
    [Recommendation.Capable]: 2,
    [Recommendation.NotRecommended]: 1,
  }

  // Score: primary count * 100, best recommendation * 10, total recommendation sum
  // This ensures: both-primary > one-primary > none-primary,
  // then top-pick beats recommended, then sum of all recommendations as final tiebreaker
  const scoredCategories = overlappingCategoryData.map((data) => {
    const appInfoValues = Array.from(data.appInfo.values())
    const primaryCount = appInfoValues.filter(
      (info) => info.classification === Classification.Primary,
    ).length
    const bestRec = Math.max(
      ...appInfoValues.map((info) => recommendationScore[info.recommendation] ?? 0),
    )
    const recSum = appInfoValues.reduce(
      (sum, info) => sum + (recommendationScore[info.recommendation] ?? 0),
      0,
    )
    const score = primaryCount * 100 + bestRec * 10 + recSum
    return { category: data.category, score }
  })

  scoredCategories.sort((a, b) => b.score - a.score)

  return scoredCategories.map((item) => item.category)
}

/**
 * Client-side port of fetchComparisonVideoByAppSlugs from fetchers.ts.
 * Finds a video linked to ALL compared apps with collectionTypes: comparison.
 *
 * Unlike the server-side version, queryPayload doesn't support dot-path where
 * clauses (e.g. 'app.slug'), so we resolve slugs → IDs first then query by ID.
 */
export async function queryComparisonVideoByAppSlugs(
  slugs: Array<App['slug']>,
): Promise<{ video: Video; videoApps: VideoAppsApp[] }[] | undefined> {
  const appCount = slugs.length
  if (appCount === 0) return undefined

  // Resolve slugs to app IDs and get comparison video IDs in parallel
  // (queryPayload can't do dot-path where clauses like 'app.slug' or 'video.collectionTypes')
  const [{ docs: appDocs }, { docs: comparisonVideos }] = await Promise.all([
    queryPayload({
      collection: 'app',
      select: { slug: true },
      where: { slug: { in: slugs } },
      pagination: false,
    }),
    queryPayload({
      collection: 'video',
      select: {},
      where: {
        collectionTypes: { equals: 'comparison' },
        datePublished: { less_than_equal: new Date().toISOString() },
      },
      pagination: false,
    }),
  ])

  if (appDocs.length !== appCount) return undefined
  const appIds = appDocs.map((a) => a.id)

  const comparisonVideoIds = new Set(comparisonVideos.map((v) => v.id))
  if (comparisonVideoIds.size === 0) return undefined

  const { docs: videoAppDocs } = await queryPayload({
    collection: 'video_apps_app',
    populate: {
      video: {
        thumbnail: true,
        name: true,
        description: true,
        url: true,
        duration: true,
        thumbnailOverride: true,
        youtubeId: true,
        collectionTypes: true,
        datePublished: true,
        createdAt: true,
      },
      app: {
        slug: true,
        name: true,
        icon: true,
        glyph: true,
        brandBackgroundColor: true,
        brandPrimaryColor: true,
      },
      media: {
        url: true,
        alt: true,
        filename: true,
        coveragePercent: true,
        xOffsetPercent: true,
        yOffsetPercent: true,
      },
    },
    where: {
      app: { in: appIds },
      video: { in: Array.from(comparisonVideoIds) },
    },
    depth: 3,
    sort: ['order'],
    pagination: false,
    cacheKey: `query:video-apps:comparison:${[...slugs].sort().join(',')}`,
  })

  const videoMap = new Map<Video['id'], { video: Video; appIds: Set<App['id']> }>()

  for (const va of videoAppDocs as VideoAppsApp[]) {
    const video = va.video as Video
    if (!video || typeof video === 'number') continue
    const app = va.app as App
    const entry = videoMap.get(video.id)
    if (!entry) {
      videoMap.set(video.id, { video, appIds: new Set([app.id]) })
    } else {
      entry.appIds.add(app.id)
    }
  }

  const matchedVideoIds = Array.from(videoMap.entries())
    .filter(([, { appIds }]) => appIds.size === appCount)
    .map(([id]) => id)

  if (matchedVideoIds.length === 0) return undefined

  return matchedVideoIds.map((videoId) => ({
    video: videoMap.get(videoId)!.video,
    videoApps: (videoAppDocs as VideoAppsApp[]).filter((va) => (va.video as Video).id === videoId),
  }))
}
